import type { EndReason, GameStatus, Seat, Ship, Shot } from '@sea-wars/game-core';
import type { Command, ServerEvent } from '@sea-wars/protocol';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { track } from '../analytics.js';
import { type ConnectionStatus, GameConnection } from './connection.js';

export interface RoomPlayer {
  readonly seat: Seat;
  readonly nickname: string;
  readonly online: boolean;
  readonly ready: boolean;
}

export interface GameView {
  readonly gameId: string | null;
  readonly status: GameStatus;
  readonly you: Seat;
  readonly turn: Seat | null;
  readonly ownFleet: Ship[] | null;
  readonly ownReady: boolean;
  /** Strzały przeciwnika w moją planszę. */
  readonly incoming: Shot[];
  /** Moje strzały w planszę przeciwnika. */
  readonly enemyShots: Shot[];
  readonly players: RoomPlayer[];
  readonly winner: Seat | null;
  readonly endReason: EndReason | null;
  readonly fleets: [Ship[], Ship[]] | null;
  /** Ostatni komunikat do odczytania przez czytnik ekranu. */
  readonly message: string;
}

export const EMPTY_VIEW: GameView = {
  gameId: null,
  status: 'placement',
  you: 0,
  turn: null,
  ownFleet: null,
  ownReady: false,
  incoming: [],
  enemyShots: [],
  players: [],
  winner: null,
  endReason: null,
  fleets: null,
  message: 'Łączenie z partią…',
};

const label = (x: number, y: number): string => `${String.fromCharCode(65 + x)}${y + 1}`;

/**
 * Akcja reduktora: zdarzenie serwera albo jedna akcja lokalna.
 *
 * Rozstawienie własnej floty jest jedyną rzeczą, którą klient wie wcześniej niż serwer,
 * i musi ją pokazać od razu — inaczej gracz nie widziałby własnych statków aż do
 * następnego snapshotu, czyli w praktyce przez całą partię.
 */
export type GameAction =
  | ServerEvent
  | { readonly type: 'local.fleetPlaced'; readonly fleet: Ship[] };

/**
 * Stan partii składany ze zdarzeń protokołu.
 *
 * Klient nigdy nie liczy wyników sam — przyjmuje rozstrzygnięcia serwera. Dzięki temu
 * nie ma tu drugiej implementacji zasad, która mogłaby rozjechać się z autorytatywną.
 */
export function reduce(state: GameView, event: GameAction): GameView {
  switch (event.type) {
    case 'local.fleetPlaced':
      return { ...state, ownFleet: event.fleet };

    case 'game.snapshot': {
      const view = event.view;
      return {
        ...state,
        gameId: view.gameId,
        status: view.status,
        you: view.you,
        turn: view.turn,
        // Serwer nigdy nie „odstawia" floty, więc brak jej w snapshocie znaczy tylko tyle,
        // że jeszcze do niego nie dotarła — nie ma powodu kasować tego, co już widzimy.
        ownFleet: view.own.fleet ?? state.ownFleet,
        ownReady: view.own.ready,
        incoming: view.own.incoming,
        enemyShots: view.enemy.shots,
        winner: view.winner,
        endReason: view.endReason,
        fleets: view.enemy.fleet
          ? view.you === 0
            ? [view.own.fleet ?? [], view.enemy.fleet]
            : [view.enemy.fleet, view.own.fleet ?? []]
          : state.fleets,
        message: describeStatus(view.status, view.turn, view.you),
      };
    }

    case 'room.state': {
      // Własna gotowość przychodzi także tędy. Bez tego po kliknięciu „Gotów"
      // edytor rozstawiania zostawałby na ekranie aż do następnego snapshotu.
      const me = event.players.find((player) => player.seat === state.you);
      return {
        ...state,
        players: [...event.players],
        ownReady: me?.ready ?? state.ownReady,
      };
    }

    case 'game.started':
      return { ...state, status: 'in_progress', message: 'Partia rozpoczęta.' };

    case 'game.turn':
      return {
        ...state,
        turn: event.seat,
        message:
          event.seat === state.you ? 'Twoja kolej — wybierz pole.' : 'Czekasz na ruch przeciwnika…',
      };

    case 'game.shot': {
      const mine = event.seat === state.you;
      const shot: Shot = {
        x: event.x,
        y: event.y,
        result: event.result,
        ...(event.sunkShip ? { shipId: event.sunkShip.id } : {}),
      };
      const halo: Shot[] = event.revealed.map((c) => ({ x: c.x, y: c.y, result: 'miss' as const }));
      const outcome =
        event.result === 'miss'
          ? 'pudło'
          : event.result === 'hit'
            ? 'trafiony!'
            : 'trafiony zatopiony!';
      return {
        ...state,
        enemyShots: mine ? [...state.enemyShots, shot, ...halo] : state.enemyShots,
        incoming: mine ? state.incoming : [...state.incoming, shot, ...halo],
        message: `${mine ? 'Strzelasz' : 'Przeciwnik strzela'} w ${label(event.x, event.y)} — ${outcome}`,
      };
    }

    case 'game.presence':
      return {
        ...state,
        players: state.players.map((player) =>
          player.seat === event.seat ? { ...player, online: event.online } : player,
        ),
      };

    case 'game.over':
      return {
        ...state,
        status: 'finished',
        turn: null,
        winner: event.winner,
        endReason: event.reason,
        fleets: event.fleets,
        message: describeEnd(event.winner, event.reason, state.you),
      };

    default:
      return state;
  }
}

function describeStatus(status: GameStatus, turn: Seat | null, you: Seat): string {
  if (status === 'placement') return 'Rozstaw flotę i zgłoś gotowość.';
  if (status === 'finished') return 'Partia zakończona.';
  return turn === you ? 'Twoja kolej — wybierz pole.' : 'Czekasz na ruch przeciwnika…';
}

function describeEnd(winner: Seat | null, reason: EndReason, you: Seat): string {
  if (reason === 'cancelled') return 'Partia anulowana — nie liczy się do statystyk.';
  if (winner === null) return 'Partia zakończona bez rozstrzygnięcia.';
  const won = winner === you;
  switch (reason) {
    case 'resign':
      return won ? 'Przeciwnik się poddał — wygrywasz!' : 'Poddałeś partię.';
    case 'abandoned':
      return won
        ? 'Przeciwnik opuścił partię — wygrywasz!'
        : 'Partia przepadła po twoim rozłączeniu.';
    case 'timeout':
      return won ? 'Przeciwnik przekroczył czas — wygrywasz!' : 'Przekroczyłeś czas na ruch.';
    default:
      return won
        ? 'Zatopiłeś całą flotę przeciwnika. Wygrana!'
        : 'Twoja flota zatopiona. Przegrana.';
  }
}

export interface UseGameResult {
  readonly view: GameView;
  readonly connection: ConnectionStatus;
  readonly error: string | null;
  readonly send: (cmd: Command) => void;
  /** Zatwierdzenie rozstawienia: pokazuje flotę od razu i wysyła obie komendy. */
  readonly placeFleet: (fleet: Ship[]) => void;
  /**
   * `/game/:id` to widok LIVE partii, nie przegląd historii (od tego jest `/replay/:id`).
   * Sygnał "wróć do lobby" dla dwóch przypadków starego/martwego linku: świeże wejście
   * (F5, otwarcie z zapamiętanego URL-a) trafia od razu na już zakończoną partię, albo
   * partia w ogóle nie istnieje / nie jest nasza. Partia, która kończy się W TRAKCIE gdy
   * już tu jesteśmy, przychodzi jako zdarzenie `game.over`, nie nowy snapshot — tego
   * rozróżnienie nie dotyczy, więc świeżo zakończona gra nadal pokazuje ekran wyniku.
   */
  readonly redirectToLobby: boolean;
}

/**
 * Podłącza komponent do partii: transport, stan i wysyłanie komend.
 *
 * Argument jest prymitywem (id partii), a nie obiektem, i to jest celowe: obiekt byłby przy każdym
 * renderze nową referencją, więc efekt zrywałby i otwierał połączenie w kółko.
 */
export function useGame(gameId: string | undefined): UseGameResult {
  const [view, dispatch] = useReducer(reduce, EMPTY_VIEW);
  const [connection, setConnection] = useState<ConnectionStatus>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [redirectToLobby, setRedirectToLobby] = useState(false);
  const connectionRef = useRef<GameConnection | null>(null);

  useEffect(() => {
    if (!gameId) return;
    setRedirectToLobby(false);
    let sawFirstSnapshot = false;
    const link = new GameConnection({
      onEvent: (evt) => {
        if (evt.type === 'game.snapshot' && !sawFirstSnapshot) {
          sawFirstSnapshot = true;
          if (evt.view.status === 'finished') setRedirectToLobby(true);
        }
        dispatch(evt);
      },
      onStatus: setConnection,
      onError: (code, message) => {
        // Partia nie istnieje albo nie jest nasza — nie ma czego tu pokazywać.
        if (code === 'ROOM_NOT_FOUND' || code === 'UNAUTHORIZED') {
          setRedirectToLobby(true);
          return;
        }
        setError(message);
        track('error_shown', { gameId, props: { code } });
      },
    });
    connectionRef.current = link;
    link.open(gameId);
    return () => {
      link.close();
      connectionRef.current = null;
    };
  }, [gameId]);

  const send = useCallback((cmd: Command) => {
    setError(null);
    connectionRef.current?.send(cmd);
  }, []);

  const placeFleet = useCallback(
    (fleet: Ship[]) => {
      dispatch({ type: 'local.fleetPlaced', fleet });
      send({ type: 'game.placeFleet', fleet });
      send({ type: 'game.ready' });
    },
    [send],
  );

  return useMemo(
    () => ({ view, connection, error, send, placeFleet, redirectToLobby }),
    [view, connection, error, send, placeFleet, redirectToLobby],
  );
}
